// @ts-nocheck
/**
 * @import { HookDataActions } from "../../hooks/useDataActions.ts"
 * @import { RowModel } from "../../models/RowModel.ts"
 * */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import invariant from 'invariant';
import noop from 'lodash/noop';
import isEqual from 'lodash/isEqual';
import { useDispatch, useSelector } from 'react-redux';
import { apiRequest, incaAccessor } from '@cancercentrum/inca';
import * as DataActions from '../../store/data/actions';
import { RegisterActions } from '../../store/register/slice';
import ErrorBoundary from '../ErrorBoundary';
import { IncaRowContext } from '../IncaRowContext';
import { useIsMounted } from '../../hooks/useIsMounted';
import { useI18n } from '../../i18n';
import DetailedWarningsModal from '../DetailedWarningsModal';
import { ValidationModal } from './ValidationModal';
import { SimplifiedErrand } from './SimplifiedErrand';
// List of supported events. Key: event name, value: prop name.
const EVENT_MAPPINGS = {
    beforeErrandTransition: 'onBeforeErrandTransition',
    beforeRecordSave: 'onBeforeRecordSave',
    connection: 'onConnection',
    // validation: 'onValidation',
    beginComparison: 'onBeginComparison',
    comparison: 'onComparison',
    errandTransition: 'onErrandTransition',
    recordSave: 'onRecordSave',
    obliteration: 'onObliteration'
};
const EMPTY_ARRAY = Object.freeze([]);
const updateFormData = (data)=>{
    const inca = incaAccessor();
    inca.form.data = data;
};
/**
 * @typedef ChildrenData
 * @type {object}
 * @property {RowModel} row
 * @property {RowModel[]} history
 * @property {RowModel[]} future
 * @property {HookDataActions} dataActions
 */ /**
 * @callback childrenCallback
 * @param {ChildrenData} data
*/ /**
 * @typedef DesignedFormProps
 * @type {object}
 * @property {childrenCallback} children
 * @property {number} [registerId]
 */ const DesignedForm = (props)=>{
    const inca = incaAccessor();
    const { t } = useI18n();
    const { registerId, errandPanelProps, children } = props;
    const [showHasErrorDialog, setShowHasErrorDialog] = useState(false);
    const propsRef = useRef();
    const rowRef = useRef();
    const historyRef = useRef();
    const isFinishedRef = useRef(false);
    const isMounted = useIsMounted();
    const [warningModalResolve, setWarningModalResolve] = useState(null);
    const [errors, setErrors] = useState([]);
    const [isFinished, setIsFinished] = useState(false);
    const { row, history, future } = useSelector((state)=>{
        if (process.env.NODE_ENV !== 'production') {
            invariant(state.data, `data is missing in the redux store. Make sure you have created the initial data state and are using the dataReducer, e.g.

import { configureStore } from '@reduxjs/toolkit';
import { dataReducer, settingsReducer, dataUtils } from '@cancercentrum/rcc-react';

const dataState = dataUtils.createDataState();

const store = configureStore({
    reducer:
        data: dataReducer,
        settings: settingsReducer,
    },
    preloadedState: { data: dataState },
});
`);
        }
        return {
            row: state.data.present,
            history: state.data.past,
            future: state.data.future
        };
    }, isEqual);
    const dispatch = useDispatch();
    propsRef.current = props;
    rowRef.current = row;
    historyRef.current = history;
    isFinishedRef.current = isFinished;
    const closeModal = ()=>{
        setShowHasErrorDialog(false);
    };
    const onValidation = useCallback((...args)=>{
        const skipValidation = propsRef.current.skipValidation || EMPTY_ARRAY;
        updateFormData(rowRef.current.toIncaJS());
        // Kolla om validering ska skippas
        if (inca.errand && inca.errand.action) {
            const id = parseInt(inca.errand.action.val(), 10);
            const text = inca.errand.action.find(':selected').text();
            if (skipValidation.indexOf(id) !== -1 || skipValidation.indexOf(text) !== -1) {
                return (propsRef.current.onValidation || noop)(...args);
            }
        }
        const { validationPredicate } = propsRef.current;
        const curErrors = rowRef.current.getErrors({
            includePristine: true,
            validationPredicate: validationPredicate
        });
        const errorsCount = curErrors.filter((x)=>x.$errors.some((y)=>y.isFatal)).length;
        const warningsCount = curErrors.filter((x)=>x.$errors.some((y)=>!y.isFatal)).length;
        dispatch(DataActions.markErrorsAsDirty({
            validationPredicate: validationPredicate
        }));
        if (errorsCount) {
            ReactDOM.unstable_batchedUpdates(()=>{
                setErrors(curErrors);
                setShowHasErrorDialog(true);
            });
            return false;
        } else if (warningsCount) {
            if (propsRef.current.useDetailedWarningsModal) {
                return new Promise((resolve)=>{
                    setWarningModalResolve(()=>resolve);
                });
            }
            if (!window.confirm(t('component.designedform.warningsAlert'))) {
                return false;
            }
        }
        return (propsRef.current.onValidation || noop)(...args);
    }, [
        propsRef,
        rowRef,
        inca,
        dispatch,
        t
    ]);
    const onBeforeWindowUnload = useCallback((e)=>{
        if (!('onBeforeWindowUnload' in propsRef.current)) {
            if (historyRef.current.length && isMounted.current && !isFinishedRef.current) {
                const message = t('component.designedform.unsavedAlert');
                e.returnValue = message;
                return message;
            }
        } else {
            const eData = {
                row: rowRef.current,
                history: historyRef.current
            };
            return (propsRef.current.onBeforeWindowUnload || noop)(e, eData);
        }
    }, [
        isMounted,
        t
    ]);
    const onBeforeFormUnload = useCallback(()=>{
        if (!('onBeforeFormUnload' in propsRef.current)) {
            if (historyRef.current.length && isMounted.current && !isFinishedRef.current) {
                return confirm(t('component.designedform.unsavedAlert'));
            }
            return true;
        }
        const eData = {
            row: rowRef.current,
            history: historyRef.current
        };
        return (propsRef.current.onBeforeFormUnload || noop)(eData);
    }, [
        isMounted,
        t
    ]);
    useEffect(()=>{
        if (registerId) {
            apiRequest(`/Registers/${registerId}/RegisterVariables/Documentation`).then((result)=>{
                dispatch(RegisterActions.addRegisterVariableDocumentation(result));
            });
        }
    }, [
        dispatch,
        registerId
    ]);
    const unsubscribeRef = useRef();
    useEffect(()=>{
        const markAsFinished = ()=>{
            setIsFinished(true);
            isFinishedRef.current = true;
        };
        const eventCallbacks = {
            errandTransition: markAsFinished,
            recordSave: markAsFinished,
            obliteration: markAsFinished,
            validation: onValidation,
            beforeFormUnload: onBeforeFormUnload,
            beginComparison () {
                inca.form.data = rowRef.current.toIncaJS();
            },
            comparison (data) {
                const keys = Object.keys(data);
                dispatch(DataActions.updateRow([], (storeRow)=>keys.reduce((r, key)=>r.setRegvarValue(key, data[key].value).setRegvarInclude(key, data[key].include), storeRow)));
            }
        };
        const eventCallbackInfo = Object.keys(EVENT_MAPPINGS).reduce((reduction, eventName)=>{
            reduction.push({
                event: eventName,
                callback (...args) {
                    const callbacks = eventCallbacks[eventName] ? [
                        eventCallbacks[eventName]
                    ] : [];
                    if (propsRef.current[EVENT_MAPPINGS[eventName]]) {
                        callbacks.push(propsRef.current[EVENT_MAPPINGS[eventName]]);
                    }
                    return callbacks.reduce((r, handler)=>{
                        return r === false ? r : handler(...args);
                    }, undefined);
                }
            });
            return reduction;
        }, []);
        eventCallbackInfo.forEach((x)=>{
            inca.on(x.event, x.callback);
        });
        inca.on('validation', onValidation);
        inca.on('beforeFormUnload', onBeforeFormUnload);
        window.top.addEventListener('beforeunload', onBeforeWindowUnload);
        unsubscribeRef.current = ()=>{
            eventCallbackInfo.forEach((x)=>{
                inca.off(x.event, x.callback);
            });
            inca.off('validation', onValidation);
            inca.off('beforeFormUnload', onBeforeFormUnload);
            window.top.removeEventListener('beforeunload', onBeforeWindowUnload);
        };
        return unsubscribeRef.current;
    }, [
        dispatch,
        inca,
        onValidation,
        onBeforeFormUnload,
        onBeforeWindowUnload
    ]);
    // När posten slutförts kan allt unmountas och få möjlighet att städa efter sig.
    if (isFinished) {
        return /*#__PURE__*/ _jsx("div", {});
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "rcc",
        children: [
            /*#__PURE__*/ _jsx("div", {
                id: "detailed-warnings-modal"
            }),
            /*#__PURE__*/ _jsxs(ErrorBoundary, {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        children: /*#__PURE__*/ _jsx(IncaRowContext, {
                            path: EMPTY_ARRAY,
                            children: ({ dataActions })=>/*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        inca.form.useSimplifiedErrandType && /*#__PURE__*/ _jsx(SimplifiedErrand, {
                                            unsubscribeRef: unsubscribeRef,
                                            options: errandPanelProps?.options,
                                            validate: onValidation,
                                            reportingPositions: errandPanelProps?.reportingPositions,
                                            topPositions: errandPanelProps?.topPositions,
                                            row: row,
                                            children: children({
                                                row,
                                                history,
                                                future,
                                                dataActions
                                            })
                                        }),
                                        !inca.form.useSimplifiedErrandType && children({
                                            row,
                                            history,
                                            future,
                                            dataActions
                                        })
                                    ]
                                })
                        })
                    }),
                    /*#__PURE__*/ _jsx(ValidationModal, {
                        errors: errors,
                        show: showHasErrorDialog,
                        onHide: closeModal
                    }),
                    !!warningModalResolve && /*#__PURE__*/ _jsx(DetailedWarningsModal, {
                        showModal: true,
                        closeModal: (result)=>{
                            warningModalResolve(result);
                            setWarningModalResolve(null);
                        }
                    })
                ]
            })
        ]
    });
};
export default DesignedForm;
